// ServiceCard.js
import React, { useState } from 'react';
import {
  Grid,
  Card,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse, // MUI Collapse for animation
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion } from 'framer-motion'; // For icon rotation and initial load
import { Link } from 'react-router-dom'; // Import Link

// --- Type Definitions ---
interface ServiceData {
  display_title: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  gradient: string;
  details: string[];
  display_details: string[];
}

interface ServiceCardProps {
  service: ServiceData;
  index: number;
  isMobile: boolean;
}

// --- Helper Function ---
const generateSlug = (text: string): string => {
  return text.toLowerCase().split(' ').join('-').replace(/[^\w-]+/g, '');
};

// --- Component ---
const ServiceCard = ({ service, index, isMobile }: ServiceCardProps) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const mainServiceSlug = generateSlug(service.title);

  const handleToggleExpand = () => {
    setIsExpanded((prevExpanded) => !prevExpanded);
  };


  // Animation for the expand icon
  const iconVariants = {
    collapsed: { rotate: 0 },
    expanded: { rotate: 180 },
  };

  return (
    <Grid item xs={12} sx={{ display: 'flex' }}>
      {/* Initial load animation for the whole card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.15 }}
        style={{ width: '100%', display: 'flex' }}
      >
        <Card
          sx={{
            width: '100%',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
            boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
            display: 'flex',
            position: 'relative',
            zIndex: 1,
            flexDirection: 'column',
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 10px 20px rgba(0,0,0,0.12)',
            },
          }}
        >
          {/* Main Clickable Header Area */}
          <Box
            onClick={handleToggleExpand}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: { xs: 2, md: 3 },
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.04),
              },
            }}
            role="button"
            aria-expanded={isExpanded}
            aria-controls={`service-details-${index}`}
          >
            {/* Icon and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mr: 1 }}>
              <Box sx={{
                p: 1.5,
                borderRadius: '50%',
                bgcolor: service.gradient,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                mr: 2,
              }}>
                {React.cloneElement(service.icon, { sx: { fontSize: isMobile ? 24 : 30, display: 'block' } })}
              </Box>
              {/* Consider making the Title itself a link to the main service page */}
              <Typography variant={isMobile ? "h6" : "h5"} component="h2" sx={{ wordBreak: 'break-word', fontWeight: 600 }}>
                {service.display_title}
              </Typography>
            </Box>

            {/* Expand Icon Button Animation */}
            <motion.div
              animate={isExpanded ? "expanded" : "collapsed"}
              variants={iconVariants}
              transition={{ duration: 0.3 }}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <ExpandMoreIcon />
            </motion.div>
          </Box>

          {/* === Collapsible Details Section === */}
          <Collapse
            in={isExpanded}
            timeout="auto"
            // unmountOnExit // Keep this for performance, but be aware of SEO implications (see notes below)
            sx={{ width: '100%' }}
            id={`service-details-${index}`}
          >
            <Box sx={{ pt: 0, pb: 2, px: 0 }}>
              <Divider variant="middle" sx={{ mx: { xs: 2, md: 3 }, mb: 1 }} />

              <Typography
                variant="body2"
                sx={{
                  px: { xs: 2, md: 3 },
                  pt: 1,
                  pb: 1,
                  color: 'text.secondary',
                  lineHeight: 1.6,
                }}>
                {service.description}
              </Typography>

              <List dense sx={{ width: '100%', p: 0, pt: 0.5 }}>
                {service.details.map((detailText, detailIndex) => {
                  const detailSlug = generateSlug(detailText);
                  const detailUrl = `/services/${mainServiceSlug}/${detailSlug}`;
                  const displayDetail = service.display_details[detailIndex];

                  return (
                    // *** SEO Improvement: Use react-router-dom Link ***
                    <Link
                      to={detailUrl}
                      key={detailIndex}
                      onClick={() => window.scrollTo(0, 0)} // Keep scroll, or handle via router
                      style={{ textDecoration: 'none', color: 'inherit' }} // Inherit color & remove underline
                    >
                      <ListItem
                        // component="div" // Let Link handle the interaction, ListItem is presentation
                        sx={{
                          py: 0.5,
                          px: { xs: 2, md: 3 },
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.action.hover, 0.06),
                            '& .MuiListItemText-primary': {
                              color: theme.palette.primary.main,
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: theme.palette.primary.main }}>
                          <CheckCircleOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={displayDetail}
                          primaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                        />
                      </ListItem>
                    </Link>
                  );
                })}
              </List>

              


            </Box>
          </Collapse>
        </Card>
      </motion.div>
    </Grid>
  );
};

export default ServiceCard;